import { Box, Modal } from "@mui/material";
import FileDrop from "components/FileDrop/FileDrop";
import { CloseBlackSVG, UploadAiSVG } from "components/SVG/SVG";
import StoryCropzone from "components/StoryCropzone/StoryCropzone";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { toastError, toastInfo } from "hooks/useToastify";
import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import ReactPlayer from "react-player";
import { editStorySegments } from "store/async-actions/Story/storyActions";
import { getHiveUuid } from "store/reducers/HiveDetails/hiveDetails";
import {
  getCurrentStoryIdSelector,
  getCurrentStoryIndexSelector,
  getStoryDescSelector,
  getStoryImageIsUploaded,
  getStoryImgUrlSelector,
  getStorySegmentsSelector,
  getStoryTitleSelector,
  getStoryUuidSelector,
  setStoryBlob,
  setStoryImageUploaded,
  setStoryImgFile,
} from "store/reducers/Story/story";
import {
  getContentTypeSelector,
  setContentType,
  setVideoFile,
  thumbnailSelector,
  uploadFileSelector,
} from "store/reducers/Videos/videos";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 522,
  height: 630,
  bgcolor: "#fff",
  border: "none",
  boxShadow: 24,
  p: 4,
  outline: "none",
  borderRadius: 2,
};

interface Props {
  openDrop: boolean;
  setOpenDrop: React.Dispatch<React.SetStateAction<boolean>>;
  file: string;
}

const StoryDropzone = ({ file, openDrop, setOpenDrop }: Props) => {
  const dispatch = useAppDispatch();

  const thumbnail = useAppSelector(thumbnailSelector);
  const uploadedVideoFile = useAppSelector(uploadFileSelector);

  useEffect(() => {
    dispatch(setStoryImgFile(""));
    dispatch(setStoryImageUploaded(false));
  }, []);

  const imageUploaded = useAppSelector(getStoryImageIsUploaded);
  const imgFile = useAppSelector(getStoryImgUrlSelector);

  const [image, setImage] = useState<string | ArrayBuffer>("");
  const handleDrop = (acceptedFiles: File[]) => {
    setImage("");
    handleCropClose();
    setSubmitClick(false);
    /// Get the first file
    const file = acceptedFiles[0];
    if (file.type === "image/png" || file.type === "image/jpeg") {
      if (file.size > 100000000) {
        toastInfo("Please upload images less than 80 mb");
      } else {
        dispatch(setContentType(file.type));
        // Create a new FileReader
        const reader = new FileReader();

        // Set the image state when the file has loaded
        reader.onload = (e) => {
          setImage(e.target?.result!);
        };

        // Read the file as a data URL
        reader.readAsDataURL(file);
        handleCropOpen();
      }
    } else {
      toastError("Invalid file type, please pass an image file .jpg or .png");
    }
  };

  const onDropAccepted = useCallback(handleDrop, []);
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDropAccepted,
  });

  const [openCrop, setOpenCrop] = useState(false);
  const handleCropClose = () => {
    setOpenCrop(false);
  };
  const handleCropOpen = () => {
    setOpenCrop(true);
  };

  const desc = useAppSelector(getStoryDescSelector);
  const title = useAppSelector(getStoryTitleSelector);
  const storyUuid = useAppSelector(getStoryUuidSelector);

  const storySegments = useAppSelector(getStorySegmentsSelector);
  const idx = useAppSelector(getCurrentStoryIndexSelector);
  const contentType = useAppSelector(getContentTypeSelector);

  const storyId = useAppSelector(getCurrentStoryIdSelector);

  const hiveUuid = useAppSelector(getHiveUuid);

  const [onSubmitClick, setSubmitClick] = useState(false);

  const handleEditSegment = () => {
    file === "video" && dispatch(setStoryBlob(uploadedVideoFile));
    dispatch(
      editStorySegments({
        description: desc,
        isActive: false,
        storyUrl: "",
        storyUuid: storyUuid,
        title: title,
        isImage: file !== "video",
        isVideo: file === "video",
        colorCode: "",
        communityUuid: hiveUuid,
        contentType: contentType,
        storyId: storyId,
        isMediaChanged: true,
        thumbnail: file === "video" ? thumbnail : "",
        actionLink: storySegments[idx].actionLink,
        order: storySegments[idx].segmentOrder,
      })
    );
    setOpenDrop(false);
  };

  const clearVideo = () => {
    dispatch(setVideoFile(null));
  };

  return (
    <Modal
      open={openDrop}
      onClose={() => setOpenDrop(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className="" sx={style}>
        {file === "video" ? (
          <>
            <div className="title_and_limit story_drop_header">
              <h3>Add Video</h3>
              <div className="pointer" onClick={() => setOpenDrop(false)}>
                <CloseBlackSVG />
              </div>
            </div>
            {!!thumbnail && !!uploadedVideoFile ? (
              <>
                <div className="story_video_view_wrapper">
                  <p
                    className="link story_video_change_title"
                    onClick={clearVideo}
                  >
                    Change
                  </p>
                  <ReactPlayer
                    width="256.5px"
                    height="456px"
                    url={URL.createObjectURL(uploadedVideoFile)}
                    controls
                    style={{
                      overflow: "hidden",
                      borderRadius: "15px",
                      background: "black",
                    }}
                  />
                  <p
                    className="story_createBtn w_full primaryBtn"
                    onClick={handleEditSegment}
                  >
                    Add Video
                  </p>
                </div>
              </>
            ) : (
              <FileDrop />
            )}
          </>
        ) : (
          <>
            <div className="title_and_limit story_drop_header">
              <h3>Add Image</h3>
              <div className="pointer" onClick={() => setOpenDrop(false)}>
                <CloseBlackSVG />
              </div>
            </div>
            <div className="cursor-pointer">
              <div
                {...getRootProps()}
                className={`upload_wrapper dropzone ${
                  isDragActive ? "dropzone--isActive border-blue-600" : ""
                } ${isDragAccept ? "dropzone--isAccept border-blue-600" : ""} ${
                  isDragReject ? "dropzone--isReject border-red-600" : ""
                }`}
              >
                <input accept="image/*" {...getInputProps()} />

                <div className="upload_story_content_wrapper">
                  {imageUploaded ? (
                    <img
                      className="uploaded_story_image"
                      src={`data:image/jpeg;base64,${imgFile}`}
                    />
                  ) : (
                    <UploadAiSVG />
                  )}
                  <div className="upload_text_wrapper">
                    <div>
                      <h4 className="">{`Drag and drop image here or click to ${
                        imageUploaded ? "change" : "upload"
                      }`}</h4>
                      <p className="text-gray-400 font-semibold text-sm">
                        Recommended size: 300kb
                      </p>
                      <p className="text-gray-400 text-sm">
                        Recommended dimensions: 173px x 221px
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {image && !onSubmitClick && (
                <StoryCropzone
                  file={file}
                  openCrop={openCrop}
                  handleCropClose={handleCropClose}
                  image={image}
                  setSubmitClick={setSubmitClick}
                />
              )}
            </div>
            {imageUploaded && (
              <div
                onClick={handleEditSegment}
                className="story_createBtn w_full primaryBtn"
              >
                Add Image
              </div>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default StoryDropzone;
